import React, { useMemo, useState, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { FlatList, StyleSheet } from "~/ui/primitives"
import { useSafeAreaInsets } from "~/ui/safe-area"
import styled, { useTheme } from "~/ui/styled"
import { observer } from "mobx-react-lite"
import { SearchChatContentScreenProps } from "./types"
import { SearchBar } from "~components/SearchBar"
import { BetweenFlex, ColumnFlex, CenterFlex, StartFlex, Text } from "~components/El"
import { useStores } from "~models"
import useNavigation from "~hooks/useNavigation"
import Avatar from "~widgets/avatar/Avatar"
import { formatMessageTime } from "~utils/format"
import { useRoute } from "~router/navigation"
import { resolveChatById, useActiveChat } from "~hooks/useActiveChat"
import { useChatSearchMessages } from "~hooks/useChatSearchMessages"
import { Loading } from "~widgets/loading"
import { getSearchableMessageContent, isSearchableMessage } from "./searchable-message"

const Container = styled(ColumnFlex)`
  flex: 1;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.blockBackground};
`

const SearchBox = styled(ColumnFlex)`
  width: 100%;
  height: auto;
  padding: 10px 12px;
`

type Row = any

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

function useSearchChatRouteId(props: SearchChatContentScreenProps): string | undefined {
  const [searchParams] = useSearchParams()
  const route = useRoute<{ chat_id?: string; chat_cid?: string }>()
  const id =
    searchParams.get("chat_id") ??
    route.params?.chat_id ??
    props.chat_id ??
    searchParams.get("chat_cid") ??
    route.params?.chat_cid ??
    props.chat_cid
  return id != null && id !== "" ? String(id) : undefined
}

const SearchChatContent: React.FC<SearchChatContentScreenProps> = (props) => {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const { chatStore, websocketStore } = useStores()
  const routeChatId = useSearchChatRouteId(props)
  const { chat: routeChat } = useActiveChat()
  const chat =
    routeChat ??
    (routeChatId ? resolveChatById(chatStore, routeChatId) : null) ??
    chatStore.current

  const { hydrationTick, isHydrating } = useChatSearchMessages(chat, websocketStore.ready)
  const [q, setQ] = useState("")

  const rows: Row[] = useMemo(() => {
    const query = q.trim().toLowerCase()
    if (!chat) return []

    const messages: any[] = chat.sortedMessages || []

    const matched = messages.filter((m) => {
      if (!m) return false
      if (!isSearchableMessage(m)) return false
      const content = getSearchableMessageContent(m)
      if (query && !content.toLowerCase().includes(query)) return false
      return true
    })

    matched.sort((a, b) => {
      const ta = typeof a.created?.getTime === "function" ? a.created.getTime() : Number(a.created || 0)
      const tb = typeof b.created?.getTime === "function" ? b.created.getTime() : Number(b.created || 0)
      return tb - ta
    })

    return matched
  }, [q, chat, hydrationTick])

  const onPressRow = useCallback(
    (message: any) => {
      navigation.navigate("Chat", {
        chat_id: chat?.chat_id ?? routeChatId,
        scrollToMessageId: message?.message_id ?? message?.id,
      })
    },
    [routeChatId, chat?.chat_id, navigation],
  )

  const renderHighlightedContent = useCallback(
    (content: string) => {
      const query = q.trim()
      if (!query) return content
      const parts = content.split(new RegExp(`(${escapeRegExp(query)})`, "ig"))
      return parts.map((part, idx) => {
        const hit = part.toLowerCase() === query.toLowerCase()
        if (!hit) return <Text key={idx}>{part}</Text>
        return (
          <Text key={idx} color="primaryText" fontWeight="700">
            {part}
          </Text>
        )
      })
    },
    [q],
  )

  const renderItem = useCallback(
    ({ item }: { item: Row }) => {
      const senderName = item?.sender?.name || item?.sender_name || ""
      const senderAvatar = item?.sender?.avatar || item?.sender_avatar || ""
      return (
        <ColumnFlex onClick={() => onPressRow(item)}>
          <BetweenFlex width="100%" padding="12px" bg={theme.colors.blockBackground} alignItems="flex-start">
            <StartFlex gap="10px" flex={1}>
              <Avatar avatar={senderAvatar} name={senderName} size={40} />
              <ColumnFlex flex={1} gap="4px">
                <Text color="text" fontWeight="600" numberOfLines={1}>
                  {senderName}
                </Text>
                <Text color="secondaryText" numberOfLines={2}>
                  {renderHighlightedContent(getSearchableMessageContent(item))}
                </Text>
              </ColumnFlex>
            </StartFlex>
            <Text color="secondaryText" fontSize="12px">
              {formatMessageTime(item.created) || ""}
            </Text>
          </BetweenFlex>
          <ColumnFlex width="100%" height={StyleSheet.hairlineWidth} bg={theme.colors.border} />
        </ColumnFlex>
      )
    },
    [onPressRow, renderHighlightedContent, theme.colors.blockBackground, theme.colors.border],
  )

  return (
    <Container>
      <SearchBox>
        <SearchBar
          value={q}
          placeholder="搜索聊天内容"
          onChange={setQ}
          onClear={() => setQ("")}
        />
      </SearchBox>

      {isHydrating && rows.length === 0 ? (
        <CenterFlex flex={1} gap="8px">
          <Loading size={20} color={theme.colors.primary} />
          <Text color="secondaryText" fontSize="13px">
            正在加载聊天记录...
          </Text>
        </CenterFlex>
      ) : (
        <FlatList
          style={{ flex: 1, width: "100%" }}
          contentContainerStyle={{ paddingBottom: insets.bottom, flexGrow: rows.length === 0 ? 1 : undefined }}
          data={rows}
          keyExtractor={(item) => item.message_id || String(item.id)}
          renderItem={renderItem}
          keyboardShouldPersistTaps="handled"
          ListEmptyComponent={
            !isHydrating ? (
              <CenterFlex flex={1} padding="40px 20px">
                <Text color="secondaryText" fontSize="13px">
                  {q.trim() ? "未找到相关聊天内容" : "暂无可搜索的聊天记录"}
                </Text>
              </CenterFlex>
            ) : null
          }
        />
      )}
    </Container>
  )
}

export default observer(SearchChatContent)
